import { useEffect, useState } from "react"
import { toast } from "react-toastify";
import { FaSpinner } from "react-icons/fa6";
import axios from "axios"
import { Link, useNavigate } from "react-router-dom"
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {Helmet} from "react-helmet-async";

const Register = () => {
    const navigate = useNavigate()
    const [showOtp, setShowOtp] = useState(false)
    const [loading, setLoading] = useState(false)
    const [timeLeft, setTimeLeft] = useState(0)
    const { t } = useTranslation()

    // Function to start the timer and store timestamp
    const startTimer = () => {
        const now = Date.now()
        const expireTime = now + 60 * 1000 // 1-minute expiry
        localStorage.setItem("otp_timer", expireTime.toString())
        setTimeLeft(60)
    }

    // Effect to check the timer on mount
    useEffect(() => {
        const storedExpireTime = localStorage.getItem("otp_timer")
        if (storedExpireTime) {
            const remainingTime = Math.floor((parseInt(storedExpireTime) - Date.now()) / 1000)
            if (remainingTime > 0) {
                setTimeLeft(remainingTime)
            }
        }

        // Timer countdown
        const interval = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(interval)
                    localStorage.removeItem("otp_timer")
                    return 0
                }
                return prev - 1
            });
        }, 1000)

        return () => clearInterval(interval)
    }, [])

    const generateOtp = async (values:any) => {
        if (values.email.trim() === "") return toast.error(t('register.email_required'))
        setLoading(true)

        try {
            // First try to login with dummy password to check account status
            const loginResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/user/login`, {
                email: values.email.trim(),
                password: "dummy_password", // dummy password
                role: "parent"
            })

            // If login succeeds unexpectedly, account exists - redirect to login
            setLoading(false)
            toast.error(t('register.account_exists'))
            setTimeout(() => {
                navigate('/login')
            }, 500)
            return
            
        } catch (loginError: any) {
            // If it's "Invalid credentials" error, account exists but password is wrong
            if (loginError.response?.data?.error === "Invalid credentials") {
                setLoading(false)
                toast.error(t('register.account_exists'))
                setTimeout(() => {
                    navigate('/login')
                }, 500)
                return
            }
            
            // For other errors (like user not found), proceed with OTP generation
            try {
                const otpResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/user/generate-otp`, {
                    email: values.email.trim(),
                })
                
                console.log("OTP response:", otpResponse)
                setShowOtp(true)
                startTimer() // Start timer after OTP request
                toast.success(t('register.otp_sent'))
            } catch (otpError: any) {
                console.log("OTP error:", otpError)
                if (otpError.response?.data?.message) {
                    toast.error(otpError.response.data.message)
                } else {
                    toast.error(t("server_error"))
                }
            } finally {
                setLoading(false)
            }
        }
    };

    const checkExist = async (values:any) => {
        if (values.email.trim() === "" || values.otp.trim() === "") return toast.error(t('register.email_otp_required'))
        setLoading(true)
        axios.post(`${process.env.REACT_APP_API_BASE_URL}/user/exist`, {
            "email": values.email.trim(),
            "otp": values.otp.trim()
        }, {headers: {'Content-Type': 'application/json', "Accept":"application/json"}, withCredentials: true }).then( res => {
            setLoading(false)
            console.log("res check exist", res)
            if (res.data.is_exist === false) {
                navigate(`/register-new?email=${values.email}`)
            } else {
                navigate(`/register-exist?email=${values.email}`)
            }
        }).catch( err => {
            setLoading(false)
            console.log("err", err.response)
            if (err.response) {
                // If the error has a response, check if it contains data and an error message
                if (err.response.data && err.response.data.error) {
                    toast.error(err.response.data.error)
                } else {
                    toast.error("An error occurred. Please try again.");
                }
            } else if (err.request) {
                // Request was made but no response was received (e.g., network issue)
                toast.error("No response from server. Please check your internet connection.");
            } else {
                // Other errors (e.g., code issues)
                toast.error("Something went wrong. Please try again.");
            }
        })
    }

    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .email(t("validation.invalid_email"))
            .required(t("validation.required")),
        otp: Yup.string().when("showOtp", {
            is: true,
            then: (schema) => schema.required(t("validation.required")),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    return (
        <>
        <Helmet>
            <title>Sign Up for MyKoku | Free Smart School Platform Malaysia</title>
            <meta name="description" content="Create your free MyKoku account today. Used by Malaysian schools to manage attendance, learning & payment. SK, SMK, SJKC & SMJK supported." />
            <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <div className="pt-4 px-4 h-screen flex flex-col">
            <div className="mb-3">
                <div className="px-0">
                    <img src={process.env.PUBLIC_URL + "/assets/img/koku.png"} className="w-20" alt="" />
                    <h3 className="text-2xl font-bold text-black mt-2">{t('register.register_parent_account')}</h3>
                </div>
            </div>

            <div className="px-0 flex-grow flex flex-col">
                <Formik
                    initialValues={{ email: "", otp: "" }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                    showOtp ? checkExist(values) : generateOtp(values);
                }}>
                    {({ isSubmitting, setFieldValue }) => (
                    <Form className="px-2 py-4 flex-grow flex flex-col">
                        <div className="mb-7">
                            <label htmlFor="emailAddress" className="text-sm font-medium text-black block mb-2">
                                {t("register.email")}
                            </label>
                            <Field
                                type="text"
                                name="email"
                                id="emailAddress"
                                placeholder={t("register.enter_email_address")}
                                disabled={showOtp}
                                className="w-full text-lg text-black border-b-2 border-gray-300 focus:border-theme outline-none py-2 disabled:opacity-60"
                                onCompositionEnd={(e:any) => {
                                    const noSpaceValue = e.target.value.replace(/\s/g, '')
                                    setFieldValue("email", noSpaceValue, true)
                                }}
                            />
                            <ErrorMessage name="email" component="div" className="mt-1 text-red-500 text-sm" />
                        </div>

                        {/* OTP field */}
                        {showOtp && (
                        <div className="mb-7">
                            <label htmlFor="otp" className="text-sm font-medium text-black block mb-2">{t("register.otp")}</label>
                            <Field
                                type="text"
                                name="otp"
                                placeholder={t("register.enter_otp")}
                                className="w-full text-lg text-black border-b-2 border-gray-300 focus:border-theme outline-none py-2"
                            />
                            <ErrorMessage name="otp" component="div" className="mt-1 text-red-500 text-sm" />
                        </div>
                        )}
                        
                        <div className="flex justify-end my-4">
                            <select
                                className="px-2 py-1 border rounded-full text-md bg-white focus:outline-none focus:ring-2 focus:ring-theme appearance-none"
                                onChange={(e) => {
                                    i18next.changeLanguage(e.target.value);
                                }}>
                                <option value="-1" disabled selected>-- Language --</option>
                                <option value="en">English</option>
                                <option value="zh">Mandarin</option>
                                <option value="ms">Bahasa</option>
                            </select>
                        </div>

                        {/* Spacer to push buttons to bottom */}
                        <div className="flex-grow"></div>

                        {/* Bottom buttons */}
                        <div className="mt-auto">
                           
                            <button
                                type="submit"
                                className="text-white text-[16px] leading-none w-full py-[15px] bg-theme border-none rounded-3xl flex items-center justify-center mb-4"
                                disabled={(!showOtp && loading) || (!showOtp && timeLeft > 0)}
                            >
                            {loading ? (
                                <FaSpinner className="animate-spin" />
                            ) : (
                                showOtp ? t('register.verify_otp') : timeLeft > 0 ? `Resend OTP (${timeLeft}s)` : t("register.send_otp")
                            )}
                            </button>

                            <div className="text-center text-gray-400 mb-4">
                                {t("register.or")}
                            </div>

                            <div className="flex items-center gap-4 mb-6">
                                <Link 
                                    to="/login" 
                                    className="text-white text-[16px] leading-none py-[15px] px-6 bg-theme border-none rounded-3xl flex items-center justify-center no-underline flex-1"
                                >
                                    {t("register.login")}
                                </Link>

                                <Link 
                                    to="/" 
                                    className="text-gray-600 text-[16px] leading-none py-[15px] px-6 border border-gray-300 rounded-3xl flex items-center justify-center no-underline flex-1"
                                >
                                    {t("register.skip_login")} <span className="ml-1">›</span>
                                </Link>
                            </div>
                        </div>
                    </Form>
                    )}
                </Formik>
            </div>
        </div>
        </>
    )
}

export default Register